'use strict';

var d3 = require('d3');
var _ = require('lodash');
var MultiBrushModel = require('./MultiBrushModelFactory')();

/**
 *
 * @param chartBrush
 * @param inactiveChartBrush
 * @returns {Function}
 * @constructor
 */
var MultiBrush = function MultiBrush(chartBrush, inactiveChartBrush) {
  return function () {
    var scales;
    var container;
    var domainRange;
    var singleSelection = false;
    var dispatch = d3.dispatch('brushchange', 'brushend');
    var keyOptions = {
      ctrlClick: true,
      shiftClick: true,
      ctrlShiftClick: true
    };
    var brush = chartBrush();
    var model = new MultiBrushModel();
    var inactiveBrush = inactiveChartBrush();

    // used to store the original brush area (used by brushend)
    var brushOldRange;

    /**
     * Updates brush extent
     */
    function updateBrush() {
      if (container) {
        container.call(brush.scales(scales).withDomainRange(domainRange));
      }
    }

    /**
     * Updates inactive brush extent
     */
    function updateInactiveBrushes() {
      if (container) {
        container.call(inactiveBrush.scales(scales).withDomainRange(domainRange).withRanges(model.getInactiveRanges()));
      }
    }

    /**
     * Updates inactive brush extent and dispatches 'brushchange' event
     */
    function updateInactiveBrushesAndDispatch(triggerEnd) {
      updateInactiveBrushes();
      var selection = [];
      if (singleSelection) {
        selection = multiBrush.getSelection()[0];
      } else {
        selection = multiBrush.getSelection();
      }
      dispatch.brushchange(selection, triggerEnd);
    }

    /**
     *
     * @param c - container
     */
    var multiBrush = function multiBrush(c) {
      container = c;
      updateBrush();
      updateInactiveBrushes();
    };

    /**
     *
     * @param range
     */
    multiBrush.inactiveClickHandler = function (range) {
      if (d3.event.ctrlKey && keyOptions.ctrlClick) {
        var brushRange = brush.getSelection();
        model.addRange(brushRange);
        brush.setSelection(range, true);
        model.removeRange(range);
      } else {
        brush.setSelection(range, true);
        model.clearRanges();
      }
      updateInactiveBrushesAndDispatch(true);
    };
    inactiveBrush.on('inactivebrushclick', multiBrush.inactiveClickHandler);

    /**
     *
     * @param range1
     * @param range2
     * @returns {*[]}
     */
    function mergeRanges(range1, range2) {
      var tmp = _.union(range1, range2);
      return [_.min(tmp), _.max(tmp)];
    }

    /**
     *
     * @param brushRange
     * @param range
     */
    function ctrlSelection(brushRange, range) {
      singleBarSelection(brushRange, range);
    }

    /**
     *
     * @param oldRange
     * @param newRange
     */
    function shiftSelection(oldRange, newRange) {
      var range = mergeRanges(oldRange, newRange);
      model.clearRanges();
      brush.setSelection(range, false);
      model.combineOverlappingRanges(range);
      updateInactiveBrushesAndDispatch();
    }

    /**
     *
     * @param oldRange
     * @param newRange
     */
    function ctrlShiftSelection(oldRange, newRange) {
      var range = mergeRanges(oldRange, newRange);
      brush.setSelection(range, false);
      model.combineOverlappingRanges(range);
      updateInactiveBrushesAndDispatch();
    }

    /**
     *
     * @param range
     */
    function crtlMoveBrush(range) {
      var evt = d3.event ? d3.event.sourceEvent : false;
      if (evt) {
        var shiftKey = evt.shiftKey;
        var ctrlKey = evt.ctrlKey;
        if (ctrlKey && !shiftKey && keyOptions.ctrlClick) {
          brushOldRange = range;
        }
      }
    }

    /**
     *
     */
    function cleanUp() {
      model.clearRanges();
      updateInactiveBrushesAndDispatch();
    }
    brush.on('brushstart', function (oldRange) {
      brushOldRange = oldRange;
    });
    brush.on('brushchange', function (range, oldRange, event) {
      if (_.isEqual(range, oldRange)) {
        return;
      }
      crtlMoveBrush(range);
      model.addRangeWhenBrushing(range, oldRange, event);
      updateInactiveBrushesAndDispatch();
    });
    brush.on('brushend', function (range) {
      var shiftKey = d3.event.sourceEvent.shiftKey;
      var ctrlKey = d3.event.sourceEvent.ctrlKey;
      if (shiftKey && ctrlKey && keyOptions.ctrlShiftClick) {
        ctrlShiftSelection(brushOldRange, range);
      } else if (shiftKey && keyOptions.shiftClick) {
        shiftSelection(brushOldRange, range);
      } else if (ctrlKey && keyOptions.ctrlClick) {
        ctrlSelection(brushOldRange, range);
      } else {
        cleanUp();
      }
      var selection = multiBrush.getSelection();
      dispatch.brushend(selection);
    });

    /**
     *
     * @returns {boolean}
     */
    multiBrush.isSelectionInitialized = function () {
      return brush.isSelectionInitialized();
    };

    /**
     *
     * @returns {*}
     */
    multiBrush.getSelection = function () {
      var result = _.cloneDeep(model.getInactiveRanges());
      result.push(brush.getSelection());
      return result;
    };

    /**
     *
     * @param range
     */
    multiBrush.updateBrush = function (range) {
      brush.updateBrush(range);
    };

    /**
     *
     * @param brushRange
     * @param range
     */
    function splitBrush(brushRange, range) {
      var split1 = [brushRange[0], range[0]];
      var split2 = [range[1], brushRange[1]];
      var split1NotEmpty = split1[0] !== split1[1];
      var split2NotEmpty = split2[0] !== split2[1];
      if (split2NotEmpty) {
        brush.setSelection(split2, true);
        if (split1NotEmpty) {
          model.addRange(split1);
        }
      } else if (split1NotEmpty) {
        brush.setSelection(split1, true);
      }
    }

    /**
     *
     * @param brushRange
     * @param range
     * @returns {boolean}
     */
    function contains(brushRange, range) {
      return brushRange[0] <= range[0] && range[1] <= brushRange[1];
    }

    /**
     *
     * @param brushRange
     * @param range
     * @param clear
     */
    function singleBarSelection(brushRange, range, clear) {
      if (clear) {
        model.clearRanges();
      } else if (!contains(brushRange, range)) {
        model.addRange(brushRange);
      }
      brush.setSelection(range, true);
      var adjustedRange = model.combineOverlappingRanges(range);
      if (!_.isEqual(range, adjustedRange)) {
        brush.setSelection(adjustedRange, true);
      }
      updateInactiveBrushesAndDispatch();
    }

    /**
     * Only for testing purposes
     */
    multiBrush.getModel = function () {
      return model;
    };

    /**
     *
     * @param range
     */
    function resetSelection(range, skipDispatchingEvent) {
      // resetting the chart
      var adjustedRange = model.combineOverlappingRanges(range);
      brush.setSelection(adjustedRange, skipDispatchingEvent);
      updateInactiveBrushes();
    }

    /**
     * @param ranges
     * @param silent
     */
    multiBrush.setInitialSelection = function (ranges, silent) {
      var mainRange = ranges;
      model.clearRanges();
      if (_.isArray(ranges) && !_.isEmpty(ranges) && _.isArray(ranges[0])) {
        mainRange = ranges[0];
        for (var i = 1; i < ranges.length; i++) {
          model.addRange(ranges[i]);
        }
      }
      brush.setSelection(mainRange, true);
      updateInactiveBrushesAndDispatch();
      dispatch.brushend(multiBrush.getSelection(), silent);
    };

    /**
     * @param range
     */
    multiBrush.setSelection = function (range, skipDispatchingEvent) {
      if (d3.event) {
        var brushRange = brush.getSelection();
        var ctrlKey = d3.event.ctrlKey;
        var shiftKey = d3.event.shiftKey;
        if (ctrlKey && shiftKey && keyOptions.ctrlShiftClick) {
          ctrlShiftSelection(brushRange, range);
        } else if (shiftKey && keyOptions.shiftClick) {
          shiftSelection(brushRange, range);
        } else if (ctrlKey && keyOptions.ctrlClick) {
          if (contains(brushRange, range)) {
            splitBrush(brushRange, range);
            updateInactiveBrushesAndDispatch();
          } else {
            ctrlSelection(brushRange, range);
          }
        } else {
          singleBarSelection(brushRange, range, true);
        }
      } else {
        resetSelection(range, skipDispatchingEvent);
      }
    };

    /**
     *  Setters and getters
     */

    multiBrush.withSingleSelection = function () {
      singleSelection = true;
      keyOptions = {
        ctrlClick: false,
        shiftClick: true,
        ctrlShiftClick: false
      };
      return multiBrush;
    };
    multiBrush.withDomainRange = function (value) {
      domainRange = value;
      return multiBrush;
    };
    multiBrush.scales = function (value) {
      scales = value;
      return multiBrush;
    };

    /**
     * Clears the extent, making the brush extent empty
     */
    multiBrush.clear = function () {
      brush.clear();
    };

    /**
     * Cleans up event handlers
     */
    multiBrush.destroy = function () {
      inactiveBrush.on('inactivebrushclick', null);
      brush.on('brushstart', null);
      brush.on('brushchange', null);
      brush.on('brushend', null);
    };
    return d3.rebind(multiBrush, dispatch, 'on');
  };
};
MultiBrush.$inject = ['ChartElementsBrush', 'ChartElementsInactiveBrush'];
module.exports = MultiBrush;